import { Node, mergeAttributes } from '@tiptap/core';

export interface IfInlineOptions {
    getVariables: () => { id: string; name: string }[];
}

// Condizione SE "in linea": stesso tag <if-block> del blocco, distinto dall'attributo inline="1".
// Il motore dell'app non distingue i due casi: tiene o scarta il contenuto tra i tag.
export const IfInline = Node.create<IfInlineOptions>({

    name: 'ifInline',

    group: 'inline',

    inline: true,

    content: 'inline*',

    addOptions() {
        return {
            getVariables: () => []
        };
    },

    addAttributes() {
        return {
            field: {
                default: ''
            },
            operator: {
                default: '=='
            },
            value: {
                default: ''
            }
        };
    },

    parseHTML() {
        return [
            {
                tag: 'if-block[inline]'
            }
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'if-block',
            mergeAttributes(HTMLAttributes, { inline: '1' }),
            0
        ];
    },

    addNodeView() {

        const getVariables = this.options.getVariables;

        return (props) => {

            const { node, editor, getPos } = props;

            // Aggiorna gli attributi per posizione (vedi IfBlock)
            const updateAttributes = (attrs: any) => {
                const pos = typeof getPos === 'function' ? getPos() : undefined;
                if (pos === undefined) {
                    return;
                }
                const current = editor.state.doc.nodeAt(pos);
                if (!current || current.type.name !== 'ifInline') {
                    return;
                }
                editor.view.dispatch(
                    editor.state.tr.setNodeMarkup(pos, undefined, { ...current.attrs, ...attrs })
                );
            };

            const dom = document.createElement('span');
            dom.className = 'if-inline';

            // =========================
            // HEADER (CONDIZIONE)
            // =========================

            const header = document.createElement('span');
            header.className = 'if-inline-header';
            header.contentEditable = 'false';

            // FIELD
            const field = document.createElement('select');

            const fields = getVariables();
            if (node.attrs['field'] && !fields.some(f => f.id === node.attrs['field'])) {
                fields.push({ id: node.attrs['field'], name: node.attrs['field'] });
            }

            fields.forEach(f => {
                const opt = document.createElement('option');
                opt.value = f.id;
                opt.innerText = f.name;
                field.appendChild(opt);
            });

            field.value = node.attrs['field'];

            field.onchange = (e: any) => {
                updateAttributes({
                    field: e.target.value
                });
            };

            // OPERATOR
            const operator = document.createElement('select');

            ['==', '!=', '<=', '>=', '<', '>', 'contains'].forEach(op => {
                const opt = document.createElement('option');
                opt.value = op;
                opt.innerText = op;
                operator.appendChild(opt);
            });

            operator.value = node.attrs['operator'];

            operator.onchange = (e: any) => {
                updateAttributes({
                    operator: e.target.value
                });
            };

            // VALUE
            const value = document.createElement('input');

            let localValue = node.attrs['value'] ?? '';
            let timeout: any;

            value.placeholder = 'value';
            value.value = localValue;
            value.size = 8;

            value.oninput = (e: any) => {
                localValue = (e.target as HTMLInputElement).value;

                clearTimeout(timeout);

                timeout = setTimeout(() => {
                    updateAttributes({
                        value: localValue
                    });
                }, 300);
            };

            header.appendChild(field);
            header.appendChild(operator);
            header.appendChild(value);

            dom.appendChild(header);

            // =========================
            // CONTENUTO CONDIZIONATO
            // =========================

            const content = document.createElement('span');
            content.className = 'if-inline-content';

            dom.appendChild(content);

            const ensureFieldOption = (id: string) => {
                if (id && !Array.from(field.options).some(o => o.value === id)) {
                    const opt = document.createElement('option');
                    opt.value = id;
                    opt.innerText = id;
                    field.appendChild(opt);
                }
            };

            return {
                dom,
                contentDOM: content,

                // Aggiorna i controlli senza ricostruire il nodo (si manterrebbe il focus dell'input)
                update: (updatedNode: any) => {
                    if (updatedNode.type !== node.type) {
                        return false;
                    }
                    ensureFieldOption(updatedNode.attrs['field']);
                    field.value = updatedNode.attrs['field'];
                    operator.value = updatedNode.attrs['operator'];
                    if (document.activeElement !== value) {
                        value.value = updatedNode.attrs['value'] ?? '';
                    }
                    return true;
                },

                // Gli eventi dei controlli dell'header sono gestiti dai controlli, non da ProseMirror
                stopEvent: (event: Event) => header.contains(event.target as any),
                ignoreMutation: (mutation: any) => header.contains(mutation.target as any)
            };
        };
    }

});
